import type { BoardDoc, Item } from '@scribbleplot/shared';

/**
 * Keyboard operation of the canvas.
 *
 * A canvas is the hardest thing to make keyboard-accessible, because its
 * contents are positions rather than a document order. The model here follows
 * the one people already know from design tools: Tab walks the objects while the
 * canvas has focus, arrows nudge what is selected, Enter opens it for editing,
 * and Escape lets go — which is also the escape hatch out of the Tab cycle, so
 * nobody gets trapped.
 */

/** Back-to-front, matching how the canvas stacks and the layers list reads. */
export const tabOrder = (doc: BoardDoc): Item[] => [...doc.items].sort((a, b) => a.z - b.z);

/** The item after (or before) the current selection in tab order. */
export function stepSelection(doc: BoardDoc, selected: readonly string[], back: boolean): string | null {
  const order = tabOrder(doc);
  if (!order.length) return null;

  const current = order.findIndex((item) => selected.includes(item.id));
  if (current === -1) return (back ? order[order.length - 1] : order[0]).id;

  const next = current + (back ? -1 : 1);
  // Stop at the ends rather than wrapping: wrapping makes it impossible to tell
  // you have reached the last object.
  if (next < 0 || next >= order.length) return null;
  return order[next].id;
}

/** How far an arrow press moves something, in world units. */
export const nudgeDistance = (shift: boolean): number => (shift ? 10 : 1);

export const ARROWS: Record<string, [number, number]> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
};

/**
 * A short, human description of an item, for the live region.
 *
 * Screen reader users get no benefit from a position, so this leads with what
 * the thing is and what it says.
 */
export function describe(item: Item): string {
  switch (item.type) {
    case 'sticky':
      return item.text ? `Sticky note, ${item.text}` : 'Empty sticky note';
    case 'text':
      return item.text ? `Text, ${item.text}` : 'Empty text';
    case 'image':
      return `Image, ${item.name || 'untitled'}`;
    case 'frame':
      return `Area, ${item.label || 'untitled'}`;
    case 'group':
      return `Group, ${item.name}, ${item.children.length} items`;
    case 'link':
      return `Link, ${item.title || item.url}`;
  }
}

/** What to announce for the current selection. */
export function announce(doc: BoardDoc, selected: readonly string[]): string {
  if (!selected.length) return 'Nothing selected';
  if (selected.length > 1) return `${selected.length} items selected`;

  const item = doc.items.find((i) => i.id === selected[0]);
  if (item) {
    const position = tabOrder(doc).findIndex((i) => i.id === item.id) + 1;
    return `${describe(item)}. ${position} of ${doc.items.length}.`;
  }
  return doc.strokes.some((s) => s.id === selected[0]) ? 'Drawing selected' : 'Selected';
}
